import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ALL_SUBJECTS, DAYS } from "@/lib/constants";
import {
  BookOpen, Plus, Trash2, Loader2, CheckCircle2, Circle, ClipboardCheck, X, RotateCcw, Layers,
} from "lucide-react";

const RES_TYPES = ["TYT/EA", "TYT", "AYT", "AYT/SAY", "AYT/SÖZ", "Genel"];

export default function Resources({ studentId, readOnly }) {
  const [plans, setPlans] = useState([]);
  const [sections, setSections] = useState([]);
  const [tarama, setTarama] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const [planDialog, setPlanDialog] = useState(false);
  const [planForm, setPlanForm] = useState({ name: "", resource_type: "TYT/EA", weeks: 4 });

  const [secForm, setSecForm] = useState({ week: 1, subject: "", unit: "", pages: "", total: "", day: "" });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ total: "", correct: "", wrong: "", blank: "" });

  const [taForm, setTaForm] = useState({ date: "", name: "", subject: "", total: "", correct: "", wrong: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, t] = await Promise.all([
        api.get(`/resource-plans/${studentId}`),
        api.get(`/resource-sections/${studentId}`),
        api.get(`/tarama-exams/${studentId}`),
      ]);
      setPlans(p.data);
      setSections(s.data);
      setTarama(t.data);
      if (!selectedId && p.data.length) setSelectedId(p.data[0].id);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId) load();
  }, [load, studentId]);

  const selected = plans.find((p) => p.id === selectedId);
  const planSections = sections.filter((s) => s.plan_id === selectedId);
  const planTarama = tarama.filter((t) => t.plan_id === selectedId);

  const createPlan = async (e) => {
    e.preventDefault();
    if (!planForm.name) return toast.error("Kaynak adı zorunlu");
    try {
      const { data } = await api.post(`/resource-plans/${studentId}`, { ...planForm, weeks: Number(planForm.weeks) });
      toast.success("Kaynak planı oluşturuldu");
      setPlanDialog(false);
      setPlanForm({ name: "", resource_type: "TYT/EA", weeks: 4 });
      setSelectedId(data.id);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const delPlan = async (id) => {
    if (!window.confirm("Bu kaynak planı ve tüm bölümleri silinsin mi?")) return;
    try {
      await Promise.all(sections.filter((s) => s.plan_id === id).map((s) => api.delete(`/resource-sections/${studentId}/${s.id}`)));
      await api.delete(`/resource-plans/${studentId}/${id}`);
      if (selectedId === id) setSelectedId(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const addSection = async (e) => {
    e.preventDefault();
    if (!secForm.subject || !secForm.unit) return toast.error("Ders ve bölüm zorunlu");
    try {
      await api.post(`/resource-sections/${studentId}`, {
        plan_id: selectedId,
        week: Number(secForm.week),
        subject: secForm.subject,
        unit: secForm.unit,
        pages: secForm.pages,
        day: secForm.day,
        total: secForm.total ? Number(secForm.total) : 0,
        correct: 0, wrong: 0, blank: 0, completed: false,
      });
      setSecForm({ ...secForm, unit: "", pages: "", total: "", day: "" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setEditForm({ total: s.total || "", correct: s.correct || "", wrong: s.wrong || "", blank: s.blank || "" });
  };

  const saveSection = async (s, complete) => {
    try {
      await api.put(`/resource-sections/${studentId}/${s.id}`, {
        plan_id: s.plan_id, week: s.week, subject: s.subject, unit: s.unit, pages: s.pages, day: s.day || "",
        total: Number(editForm.total || 0), correct: Number(editForm.correct || 0),
        wrong: Number(editForm.wrong || 0), blank: Number(editForm.blank || 0), completed: complete,
      });
      setEditId(null);
      toast.success(complete ? "Bölüm tamamlandı" : "Güncellendi");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const toggleBack = async (s) => {
    try {
      await api.put(`/resource-sections/${studentId}/${s.id}`, { ...s, completed: false });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const delSection = async (id) => {
    try {
      await api.delete(`/resource-sections/${studentId}/${id}`);
      setSections((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const addTarama = async (e) => {
    e.preventDefault();
    if (!taForm.subject) return toast.error("Ders zorunlu");
    try {
      await api.post(`/tarama-exams/${studentId}`, {
        plan_id: selectedId, date: taForm.date, name: taForm.name, subject: taForm.subject,
        total: Number(taForm.total || 0), correct: Number(taForm.correct || 0), wrong: Number(taForm.wrong || 0),
      });
      setTaForm({ date: "", name: "", subject: "", total: "", correct: "", wrong: "" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const delTarama = async (id) => {
    try {
      await api.delete(`/tarama-exams/${studentId}/${id}`);
      setTarama((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  // tarama evaluation per subject
  const evalMap = {};
  planTarama.forEach((t) => {
    const k = t.subject || "Genel";
    const a = (evalMap[k] = evalMap[k] || { subject: k, total: 0, correct: 0, wrong: 0 });
    a.total += Number(t.total || 0);
    a.correct += Number(t.correct || 0);
    a.wrong += Number(t.wrong || 0);
  });
  const evalRows = Object.values(evalMap).map((a) => {
    const acc = a.total ? Math.round((a.correct / a.total) * 100) : 0;
    let label = "Zayıf", cls = "bg-rose-50 text-rose-700 border-rose-200";
    if (acc >= 70) { label = "İyi"; cls = "bg-emerald-50 text-emerald-700 border-emerald-200"; }
    else if (acc >= 50) { label = "Orta"; cls = "bg-amber-50 text-amber-700 border-amber-200"; }
    return { ...a, acc, label, cls, net: (a.correct - a.wrong / 4).toFixed(2) };
  });

  const done = planSections.filter((s) => s.completed).length;
  const pct = planSections.length ? Math.round((done / planSections.length) * 100) : 0;

  if (loading)
    return <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-bold text-slate-900">Kaynak Takibi (Haftalık Plan)</h3>
          <p className="text-sm text-slate-500 mt-0.5">Kaynağı haftalık bölümlere ayır; bitince yeşile döner, soru/doğru/yanlış girilir. Sonrasında tarama sınavı ile konu değerlendirmesi yapılır.</p>
        </div>
        {!readOnly && (
          <Button onClick={() => setPlanDialog(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-plan-btn">
            <Plus size={16} /> <span className="ml-1">Yeni Kaynak Planı</span>
          </Button>
        )}
      </div>

      {/* plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {plans.map((p) => {
          const ps = sections.filter((s) => s.plan_id === p.id);
          const d = ps.filter((s) => s.completed).length;
          const pc = ps.length ? Math.round((d / ps.length) * 100) : 0;
          return (
            <button
              key={p.id}
              onClick={() => setSelectedId(p.id)}
              data-testid={`plan-${p.id}`}
              className={`text-left rounded-xl border p-4 transition-all ${
                selectedId === p.id ? "border-indigo-400 bg-indigo-50 ring-2 ring-indigo-500/20" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center"><BookOpen className="text-indigo-600" size={18} /></div>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{p.name}</div>
                    <div className="text-[11px] text-slate-500">{p.resource_type} · {p.weeks} hafta</div>
                  </div>
                </div>
                {!readOnly && (
                  <span onClick={(e) => { e.stopPropagation(); delPlan(p.id); }} className="text-slate-400 hover:text-rose-600 cursor-pointer"><Trash2 size={15} /></span>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Progress value={pc} className="h-2 flex-1" />
                <span className="text-xs font-mono text-slate-600">%{pc}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{d}/{ps.length} bölüm bitti</div>
            </button>
          );
        })}
        {plans.length === 0 && <div className="card-ta p-8 text-center text-slate-500 col-span-full">Henüz kaynak planı yok.</div>}
      </div>

      {selected && (
        <div className="space-y-5">
          <div className="card-ta p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Layers className="text-indigo-600" size={20} />
              <div>
                <div className="font-display font-bold text-slate-900">{selected.name}</div>
                <div className="text-xs text-slate-500">{selected.resource_type} · {selected.weeks} haftalık plan</div>
              </div>
            </div>
            <div className="flex items-center gap-2 min-w-[180px]">
              <Progress value={pct} className="h-2.5 w-32" />
              <span className="text-sm font-mono text-slate-700">%{pct}</span>
            </div>
          </div>

          {/* add section */}
          {!readOnly && (
            <form onSubmit={addSection} className="card-ta p-4">
              <div className="text-sm font-semibold text-slate-700 mb-3">Bölüm Ekle</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <div>
                  <label className="ta-label">Hafta</label>
                  <select className="ta-input" value={secForm.week} onChange={(e) => setSecForm({ ...secForm, week: e.target.value })} data-testid="sec-week">
                    {Array.from({ length: selected.weeks }, (_, i) => i + 1).map((w) => <option key={w} value={w}>{w}. Hafta</option>)}
                  </select>
                </div>
                <div>
                  <label className="ta-label">Ders</label>
                  <select className="ta-input" value={secForm.subject} onChange={(e) => setSecForm({ ...secForm, subject: e.target.value })} data-testid="sec-subject">
                    <option value="">Seç</option>
                    {ALL_SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="ta-label">Bölüm/Ünite</label>
                  <input className="ta-input" value={secForm.unit} onChange={(e) => setSecForm({ ...secForm, unit: e.target.value })} placeholder="Örn: 1.Bölüm" data-testid="sec-unit" />
                </div>
                <div>
                  <label className="ta-label">Sayfa</label>
                  <input className="ta-input" value={secForm.pages} onChange={(e) => setSecForm({ ...secForm, pages: e.target.value })} placeholder="9-20 SYF" data-testid="sec-pages" />
                </div>
                <div>
                  <label className="ta-label">Soru (ops.)</label>
                  <input className="ta-input" type="number" value={secForm.total} onChange={(e) => setSecForm({ ...secForm, total: e.target.value })} />
                </div>
              </div>
              <div className="mt-3">
                <label className="ta-label">Haftanın Günü</label>
                <div className="flex flex-wrap gap-1.5">
                  {DAYS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setSecForm({ ...secForm, day: secForm.day === d ? "" : d })}
                      data-testid={`sec-day-${d}`}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        secForm.day === d
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                          : "bg-white text-slate-600 border-slate-300 hover:border-indigo-400 hover:text-indigo-600"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <Button type="submit" className="mt-3 bg-slate-800 hover:bg-slate-900" data-testid="sec-add-btn"><Plus size={15} /><span className="ml-1">Bölüm Ekle</span></Button>
            </form>
          )}

          {/* weekly grid */}
          <div className="space-y-4">
            {Array.from({ length: selected.weeks }, (_, i) => i + 1).map((w) => {
              const items = planSections.filter((s) => Number(s.week) === w);
              return (
                <div key={w} className="card-ta p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full border border-indigo-200">{w}. Hafta</span>
                    <span className="text-xs text-slate-400">{items.filter((x) => x.completed).length}/{items.length} bitti</span>
                  </div>
                  {items.length === 0 ? (
                    <div className="text-sm text-slate-400 py-2">Bu haftaya bölüm eklenmedi.</div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
                      {DAYS.map((d) => {
                        const dayItems = items.filter((s) => s.day === d);
                        const colItems = d === DAYS[0] ? [...items.filter((s) => !s.day), ...dayItems] : dayItems;
                        return (
                          <div key={d} className="rounded-lg bg-slate-50 border border-slate-200 p-2 min-h-[90px]">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 text-center pb-1.5 mb-1.5 border-b border-slate-200" data-testid={`day-col-${d}`}>{d}</div>
                            <div className="space-y-2">
                              {colItems.length === 0 && <div className="text-[10px] text-slate-300 text-center py-3">—</div>}
                              {colItems.map((s) => (
                        <div key={s.id} className={`rounded-lg border p-3 transition-colors ${s.completed ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`} data-testid={`section-${s.id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-1.5">
                              {s.completed ? <CheckCircle2 className="text-emerald-600" size={16} /> : <Circle className="text-slate-300" size={16} />}
                              <span className="text-sm font-semibold text-slate-900">{s.subject}</span>
                            </div>
                            {!readOnly && <button onClick={() => delSection(s.id)} className="text-slate-300 hover:text-rose-600"><Trash2 size={14} /></button>}
                          </div>
                          <div className="text-xs text-slate-600 mt-1">{s.unit} {s.pages ? `· Föy ${s.pages}` : ""}{s.bank_pages ? ` · SB ${s.bank_pages}` : ""}</div>

                          {editId !== s.id && (
                            <div className="mt-1.5 grid grid-cols-4 gap-1 text-center" data-testid={`section-stats-${s.id}`}>
                              <div className="rounded bg-slate-100 py-1"><div className="text-[9px] text-slate-500">Toplam</div><div className="text-xs font-bold text-slate-800">{s.total || 0}</div></div>
                              <div className="rounded bg-emerald-50 py-1"><div className="text-[9px] text-emerald-600">Doğru</div><div className="text-xs font-bold text-emerald-700">{s.correct || 0}</div></div>
                              <div className="rounded bg-rose-50 py-1"><div className="text-[9px] text-rose-600">Yanlış</div><div className="text-xs font-bold text-rose-700">{s.wrong || 0}</div></div>
                              <div className="rounded bg-slate-100 py-1"><div className="text-[9px] text-slate-500">Boş</div><div className="text-xs font-bold text-slate-700">{s.blank || 0}</div></div>
                            </div>
                          )}

                          {editId === s.id ? (
                            <div className="mt-2 space-y-2">
                              <div className="grid grid-cols-2 gap-1.5">
                                {["total", "correct", "wrong", "blank"].map((f) => (
                                  <input key={f} type="number" className="ta-input px-1.5 py-1 text-xs" placeholder={{ total: "Top", correct: "D", wrong: "Y", blank: "Boş" }[f]}
                                    value={editForm[f]} onChange={(e) => setEditForm({ ...editForm, [f]: e.target.value })} data-testid={`edit-${f}-${s.id}`} />
                                ))}
                              </div>
                              <div className="flex gap-1.5">
                                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={() => saveSection(s, true)} data-testid={`save-complete-${s.id}`}>Bitir & Kaydet</Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditId(null)}><X size={13} /></Button>
                              </div>
                            </div>
                          ) : s.completed ? (
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-indigo-700 font-semibold">Net: {Number(s.net || 0).toFixed(2)}</span>
                                <span className="text-emerald-600 font-semibold">Tamamlandı</span>
                              </div>
                              {!readOnly && (
                                <button onClick={() => toggleBack(s)} className="mt-2 text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1"><RotateCcw size={12} /> Geri al</button>
                              )}
                            </div>
                          ) : (
                            !readOnly && (
                              <Button size="sm" className="mt-2 h-7 text-xs w-full bg-indigo-600 hover:bg-indigo-700" onClick={() => openEdit(s)} data-testid={`complete-${s.id}`}>
                                <CheckCircle2 size={13} /> <span className="ml-1">Tamamla</span>
                              </Button>
                            )
                          )}
                        </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Tarama Sınavı */}
          <div className="card-ta p-4">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardCheck className="text-indigo-600" size={18} />
              <h4 className="font-display font-bold text-slate-900">Tarama Sınavı & Konu Değerlendirmesi</h4>
            </div>
            {!readOnly && (
              <form onSubmit={addTarama} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                <div><label className="ta-label">Tarih</label><input type="date" className="ta-input" value={taForm.date} onChange={(e) => setTaForm({ ...taForm, date: e.target.value })} data-testid="ta-date" /></div>
                <div className="col-span-2"><label className="ta-label">Sınav Adı</label><input className="ta-input" value={taForm.name} onChange={(e) => setTaForm({ ...taForm, name: e.target.value })} placeholder="1. Tarama" /></div>
                <div><label className="ta-label">Ders</label>
                  <select className="ta-input" value={taForm.subject} onChange={(e) => setTaForm({ ...taForm, subject: e.target.value })} data-testid="ta-subject">
                    <option value="">Seç</option>{ALL_SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div><label className="ta-label">Doğru</label><input type="number" className="ta-input" value={taForm.correct} onChange={(e) => setTaForm({ ...taForm, correct: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="ta-label">Yanlış</label><input type="number" className="ta-input" value={taForm.wrong} onChange={(e) => setTaForm({ ...taForm, wrong: e.target.value })} /></div>
                  <div><label className="ta-label">Top.</label><input type="number" className="ta-input" value={taForm.total} onChange={(e) => setTaForm({ ...taForm, total: e.target.value })} /></div>
                </div>
                <div className="col-span-full"><Button type="submit" className="bg-slate-800 hover:bg-slate-900" data-testid="ta-add-btn"><Plus size={15} /><span className="ml-1">Tarama Ekle</span></Button></div>
              </form>
            )}

            {evalRows.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Konu Değerlendirmesi (başarı oranına göre)</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {evalRows.sort((a, b) => a.acc - b.acc).map((r) => (
                    <div key={r.subject} className={`rounded-lg border p-3 ${r.cls}`} data-testid={`eval-${r.subject}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">{r.subject}</span>
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-white/60">{r.label}</span>
                      </div>
                      <div className="text-2xl font-extrabold font-mono mt-1">%{r.acc}</div>
                      <div className="text-[11px] opacity-80">Net {r.net} · {r.correct}D/{r.wrong}Y</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {planTarama.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="px-3 py-2 font-medium">Tarih</th><th className="px-3 py-2 font-medium">Sınav</th><th className="px-3 py-2 font-medium">Ders</th>
                    <th className="px-3 py-2 font-medium">Top</th><th className="px-3 py-2 font-medium">D</th><th className="px-3 py-2 font-medium">Y</th><th className="px-3 py-2 font-medium">Net</th>{!readOnly && <th />}
                  </tr></thead>
                  <tbody>
                    {planTarama.map((t) => (
                      <tr key={t.id} className="border-b border-slate-100">
                        <td className="px-3 py-2">{t.date || "-"}</td><td className="px-3 py-2">{t.name || "-"}</td><td className="px-3 py-2">{t.subject}</td>
                        <td className="px-3 py-2">{t.total}</td><td className="px-3 py-2 text-emerald-700">{t.correct}</td><td className="px-3 py-2 text-rose-700">{t.wrong}</td>
                        <td className="px-3 py-2 font-mono font-semibold text-indigo-700">{Number(t.net || 0).toFixed(2)}</td>
                        {!readOnly && <td className="px-3 py-2 text-right"><button onClick={() => delTarama(t.id)} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {planDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setPlanDialog(false)} />
          <form onSubmit={createPlan} className="relative card-ta w-full max-w-md p-6 space-y-4" data-testid="plan-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-slate-900">Yeni Kaynak Planı</h3>
              <button type="button" onClick={() => setPlanDialog(false)}><X className="text-slate-400" size={20} /></button>
            </div>
            <div><label className="ta-label">Kaynak Adı</label><input className="ta-input" value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} placeholder="Bilgi Sarmalı / ÇAP Plus" data-testid="plan-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="ta-label">Tür</label>
                <select className="ta-input" value={planForm.resource_type} onChange={(e) => setPlanForm({ ...planForm, resource_type: e.target.value })} data-testid="plan-type">
                  {RES_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><label className="ta-label">Kaç Haftada Bitecek</label><input type="number" min="1" max="52" className="ta-input" value={planForm.weeks} onChange={(e) => setPlanForm({ ...planForm, weeks: e.target.value })} data-testid="plan-weeks" /></div>
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="plan-save">Oluştur</Button>
          </form>
        </div>
      )}
    </div>
  );
}
